import type { JSXElementConstructor, ReactElement } from 'react'
import { Children, cloneElement, createContext, Fragment, useContext } from 'react'
import { Divider } from '~/components/shared'
import { classNames } from '~/helpers/string'

const StepperContext = createContext<{ activeStep: number }>({ activeStep: 0 })
const useStepper = () => useContext(StepperContext)

type StepperProps = {
  activeStep: number // zero-based
  children: React.ReactNode // <Step /> components
  connector?: React.ReactNode
  className?: string
}

type StepPropsFromStepper = {
  index: number
  last: boolean
}

function Stepper(props: StepperProps) {
  const { activeStep, children, connector, className = '' } = props

  const childrenArray = Children.toArray(children).filter(Boolean)

  const steps = childrenArray.map((step, index) => {
    const s = step as ReactElement<any, string | JSXElementConstructor<any>>
    return cloneElement(s, {
      index,
      last: index + 1 === childrenArray.length,
      ...s.props,
    })
  })

  const Connector = connector ? (
    <div className="translate-y-[20px] transform">{connector}</div>
  ) : (
    <Divider className={` flex-1 translate-y-[20px] transform`} />
  )

  return (
    <StepperContext.Provider value={{ activeStep }}>
      <div className={classNames('flex w-full justify-between pb-10', className)}>
        {steps.map((step, i) => (
          <Fragment key={i}>
            {i !== 0 ? Connector : null}
            {step}
          </Fragment>
        ))}
      </div>
    </StepperContext.Provider>
  )
}

type StepProps = {
  children: string
  completed?: boolean
  className?: string
  onClick?: VoidFunction
  disabled?: boolean
}

export function Step(props: StepProps) {
  const {
    children,
    completed: completedProp,
    index,
    onClick,
    className = '',
    disabled = false
  } = props as StepProps & StepPropsFromStepper
  const { activeStep } = useStepper()

  let active = activeStep === index
  let completed = false

  if (activeStep > index) {
    completed = completedProp !== undefined ? completedProp : true
  }

  return (
    <div className={classNames(
      'inline-flex min-w-[100px] flex-col items-center',
      disabled ? 'opacity-60 pointer-events-none' : '',
      className
    )}>
      <div
        className={classNames(
          `text-md flex h-7 w-7 items-center justify-center rounded-full md:h-10 md:w-10 md:text-xl`,
          active ? 'bg-brand-purple-700' : completed ? 'bg-brand-purple-50' : 'bg-brand-neutral-200',
          active ? 'text-brand-white' : 'text-brand-violet-950',
        )}
        onClick={onClick}
      >
        {index + 1}
      </div>
      <div
        className={classNames(
          'mt-2.5 text-center text-xs md:text-sm',
          active ? 'text-brand-purple-700' : 'text-brand-violet-950',
        )}
      >
        {children}
      </div>
    </div>
  )
}

export default Stepper
